import { buildGs1DigitalLinkUrl } from '@myplayer/gs1';
import bwipjs from 'bwip-js';
import {
  QR_EXPORT_FORMATS,
  QR_X_DIMENSION_DEFAULT_MM,
  QR_X_DIMENSION_MAX_MM,
  QR_X_DIMENSION_MIN_MM,
  QR_X_DIMENSION_OPTIONS,
  buildVectorEpsDocument,
  encodeRgbaToPngBytes,
  getQrExportMimeType,
  rasterizeQrToRgba,
  type QrExportFormat,
  type QrExportMimeType,
  type QrXDimensionOption,
} from './qr-export-formats.js';
import {
  buildVectorPdfDocument,
  extractModuleCellsFromSvgPath,
  measureQrSvgGeometry,
} from './qr-svg-geometry.js';

export {
  QR_EXPORT_FORMATS,
  QR_X_DIMENSION_DEFAULT_MM,
  QR_X_DIMENSION_MAX_MM,
  QR_X_DIMENSION_MIN_MM,
  QR_X_DIMENSION_OPTIONS,
  getQrExportMimeType,
  type QrExportFormat,
  type QrExportMimeType,
  type QrXDimensionOption,
};
export { extractModuleCellsFromSvgPath, measureQrSvgGeometry };

/**
 * GS1 General Specifications Release 26.0 §5.12.3.1 (p412, Table 5-46) and §5.12.3.3 (p417, Table 5-49)
 * Symbol Specification Tables for QR Code carrying GS1 Digital Link URI (`bcid: 'gs1dlqrcode'`).
 *
 * Citations:
 * 1. Symbology: QR Code Model 2 per ISO/IEC 18004 carrying the uncompressed GS1 Digital Link URI
 *    (GS1 General Specifications Release 26.0 §2.1.13.1, p122; §5.12.3.1 Table 5-46 note (**), p412; §5.12.3.3 Table 5-49 note (**), p417).
 * 2. Table 5-46 ("Symbol specification table 1 addendum 2 — 2D barcodes on retail consumer items scanned at POS",
 *    GS1 General Specifications Release 26.0 §5.12.3.1, p412):
 *    - X-dimension: minimum 0.396 mm, target 0.495 mm (default), maximum 0.990 mm
 *    - Quiet Zone: minimum 4X on all four sides
 *    - Minimum ISO/IEC 15415 quality grade: 1.5/12/660
 * 3. Table 5-49 ("Symbol specification table 3 addendum 1 — 2D barcodes on trade items scanned at POS and in general distribution",
 *    GS1 General Specifications Release 26.0 §5.12.3.3, p417):
 *    - X-dimension: minimum 0.743 mm, target 0.990 mm, maximum 0.990 mm
 *    - Quiet Zone: minimum 4X on all four sides
 *    - Minimum ISO/IEC 15415 quality grade: 1.5/20/660
 */
export type Gs1SymbolSpecTableId = 'TABLE_5_46' | 'TABLE_5_49';

export interface Gs1QrSymbolSpec {
  readonly tableId: Gs1SymbolSpecTableId;
  readonly tableNumber: '5-46' | '5-49';
  readonly clauseRef: string;
  readonly pageNumber: number;
  readonly xDimensionMinMm: number;
  readonly xDimensionTargetMm: number;
  readonly xDimensionMaxMm: number;
  readonly quietZoneModules: 4;
  readonly minimumQualityGrade: string;
}

export const GS1_QR_SYMBOL_SPECS: Readonly<Record<Gs1SymbolSpecTableId, Gs1QrSymbolSpec>> = {
  TABLE_5_46: {
    tableId: 'TABLE_5_46',
    tableNumber: '5-46',
    clauseRef:
      'GS1 General Specifications Release 26.0 §5.12.3.1, Table 5-46 (Symbol specification table 1 addendum 2 — retail consumer items scanned at POS), p412',
    pageNumber: 412,
    xDimensionMinMm: 0.396,
    xDimensionTargetMm: 0.495,
    xDimensionMaxMm: 0.99,
    quietZoneModules: 4,
    minimumQualityGrade: '1.5/12/660',
  },
  TABLE_5_49: {
    tableId: 'TABLE_5_49',
    tableNumber: '5-49',
    clauseRef:
      'GS1 General Specifications Release 26.0 §5.12.3.3, Table 5-49 (Symbol specification table 3 addendum 1 — trade items scanned at POS and in general distribution), p417',
    pageNumber: 417,
    xDimensionMinMm: 0.743,
    xDimensionTargetMm: 0.99,
    xDimensionMaxMm: 0.99,
    quietZoneModules: 4,
    minimumQualityGrade: '1.5/20/660',
  },
} as const;

export interface GenerateGs1QrPrintAssetsInput {
  readonly host: string;
  readonly ai: string;
  readonly primaryKey: string;
  readonly qualifiers?: Readonly<Record<string, string>>;
  readonly tableId?: Gs1SymbolSpecTableId;
  readonly xDimensionMm?: number;
  readonly errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
}

export interface GeneratedGs1QrPrintAssets {
  readonly url: string;
  readonly bcid: 'gs1dlqrcode';
  readonly spec: Gs1QrSymbolSpec;
  readonly xDimensionMm: number;
  readonly quietZoneModules: 4;
  readonly moduleCountWithQuietZone: number;
  readonly physicalWidthMm: number;
  readonly physicalHeightMm: number;
  readonly svg: string;
  readonly pdf: string;
  readonly png: Uint8Array;
  readonly eps: string;
}

/**
 * Generates print-ready SVG, PDF, PNG, and EPS QR assets (100% black `#000000`, 4X quiet zone)
 * for a product's GS1 Digital Link URL per GS1 General Specifications Release 26.0
 * §5.12.3.1 Table 5-46 (p412: min 0.396 mm, target 0.495 mm, max 0.990 mm, quiet zone 4X, grade 1.5/12/660)
 * or §5.12.3.3 Table 5-49 (p417: min 0.743 mm, target 0.990 mm, max 0.990 mm, quiet zone 4X, grade 1.5/20/660).
 */
export function generateGs1QrPrintAssets(
  input: GenerateGs1QrPrintAssetsInput,
): GeneratedGs1QrPrintAssets {
  const tableId: Gs1SymbolSpecTableId = input.tableId ?? 'TABLE_5_46';
  const spec = GS1_QR_SYMBOL_SPECS[tableId];
  const xDimensionMm = input.xDimensionMm ?? spec.xDimensionTargetMm;

  if (xDimensionMm < spec.xDimensionMinMm || xDimensionMm > spec.xDimensionMaxMm) {
    throw new Error(
      `X-dimension ${xDimensionMm.toFixed(3)} mm is outside the permitted range [${spec.xDimensionMinMm.toFixed(3)} mm, ${spec.xDimensionMaxMm.toFixed(3)} mm] for ${spec.clauseRef}.`,
    );
  }

  const url = buildGs1DigitalLinkUrl({
    host: input.host,
    ai: input.ai,
    primaryKey: input.primaryKey,
    ...(input.qualifiers !== undefined ? { qualifiers: input.qualifiers } : {}),
  });

  const eclevel = input.errorCorrectionLevel ?? 'M';
  const quietZoneModules = spec.quietZoneModules;

  const rawSvg = bwipjs.toSVG({
    bcid: 'gs1dlqrcode',
    text: url,
    paddingwidth: 8,
    paddingheight: 8,
    barcolor: '000000',
    ...({ eclevel } as Record<string, unknown>),
  });

  const geometry = measureQrSvgGeometry(rawSvg);
  const viewBoxWidth = geometry.viewBoxSize;
  const moduleCountWithQuietZone = geometry.moduleCountWithQuietZone;
  const physicalSizeMm = Number((moduleCountWithQuietZone * xDimensionMm).toFixed(3));

  const annotatedSvg = rawSvg.replace(
    '<svg ',
    `<svg width="${physicalSizeMm.toFixed(3)}mm" height="${physicalSizeMm.toFixed(3)}mm" data-gs1-table="${spec.tableNumber}" data-gs1-page="${String(spec.pageNumber)}" data-gs1-x-dim-mm="${xDimensionMm.toFixed(3)}" data-gs1-quiet-zone="4X" data-gs1-dl-url="${url}" `,
  );

  const pathMatch = /<path[^>]*\bd="([^"]+)"/.exec(rawSvg);
  const svgPathD = pathMatch?.[1] ?? '';

  const pdf = buildVectorPdfDocument({
    url,
    clauseRef: spec.clauseRef,
    xDimensionMm,
    physicalSizeMm,
    viewBoxSize: viewBoxWidth,
    svgPathD,
  });

  const eps = buildVectorEpsDocument({
    url,
    clauseRef: spec.clauseRef,
    xDimensionMm,
    physicalSizeMm,
    viewBoxSize: viewBoxWidth,
    svgPathD,
  });

  const cells = extractModuleCellsFromSvgPath(
    svgPathD,
    geometry.moduleSize,
    moduleCountWithQuietZone,
  );
  const { rgba, size } = rasterizeQrToRgba({
    moduleCount: moduleCountWithQuietZone,
    cells,
    pixelsPerModule: 8,
  });
  return {
    url,
    bcid: 'gs1dlqrcode',
    spec,
    xDimensionMm,
    quietZoneModules,
    moduleCountWithQuietZone,
    physicalWidthMm: physicalSizeMm,
    physicalHeightMm: physicalSizeMm,
    svg: annotatedSvg,
    pdf,
    png: encodeRgbaToPngBytes(rgba, size, size),
    eps,
  };
}

/**
 * Renders the exact same `gs1dlqrcode` symbol parameters to a PNG buffer via `bwip-js`
 * for automated QR round-trip decode verification in tests.
 */
export async function renderGs1QrToPngBuffer(input: GenerateGs1QrPrintAssetsInput): Promise<{
  readonly url: string;
  readonly pngBuffer: Buffer;
}> {
  const url = buildGs1DigitalLinkUrl({
    host: input.host,
    ai: input.ai,
    primaryKey: input.primaryKey,
    ...(input.qualifiers !== undefined ? { qualifiers: input.qualifiers } : {}),
  });

  const pngBuffer = await (bwipjs.toBuffer({
    bcid: 'gs1dlqrcode',
    text: url,
    paddingwidth: 8,
    paddingheight: 8,
    scale: 4,
    barcolor: '000000',
    backgroundcolor: 'FFFFFF',
    ...({ eclevel: input.errorCorrectionLevel ?? 'M' } as Record<string, unknown>),
  }) as Promise<Buffer>);

  return { url, pngBuffer };
}

/**
 * Triggers a browser file download for an SVG, PDF, PNG, EPS, or CSV asset.
 */
export function triggerBrowserDownload(
  filename: string,
  content: string | Uint8Array,
  mimeType: QrExportMimeType | 'text/csv;charset=utf-8',
): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blobPart: BlobPart =
    typeof content === 'string' ? content : (new Uint8Array(content) as unknown as BlobPart);
  const objectUrl = URL.createObjectURL(new Blob([blobPart], { type: mimeType }));
  const anchor = Object.assign(document.createElement('a'), {
    href: objectUrl,
    download: filename,
  });
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

export {
  generateStyledQrAssets,
  verifyStyledQrDecode,
  type GeneratedStyledQrAssets,
} from './qr-styled-generator.js';
export {
  DEFAULT_SAFE_QR_STYLE,
  STYLED_QR_DISCLAIMER_LABEL,
  evaluateQrStyleRules,
  validateStyledQrRules,
  type QrDotShape,
  type QrErrorCorrectionLevel,
  type QrLogoScaleOption,
  type QrStyleConfig,
  type QrStyleRuleEvaluation,
} from './qr-style-rules.js';
